import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  Pencil, 
  Plus, 
  X, 
  Search,
  Building2,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronRight,
  Grid,
  Table,
  ChevronDown
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { fetchDepartments } from "../actions/DepartmentActions";
import { postDepartment } from "../services/departmentService";

// Department Modal Component
function DepartmentModal({
  editingDept,
  deptCode,
  setDeptCode,
  deptName,
  setDeptName,
  deptStatus,
  setDeptStatus,
  modalLoading,
  modalError,
  modalSuccess,
  saveDept,
  setIsModalOpen,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full border border-gray-100 animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-50 rounded-lg">
              {editingDept ? (
                <Pencil size={16} className="text-blue-600" />
              ) : (
                <Plus size={16} className="text-blue-600" />
              )}
            </div>
            <h2 className="text-base font-semibold text-gray-900">
              {editingDept ? "Edit Department" : "Add Department"}
            </h2>
          </div>
          <button
            onClick={() => setIsModalOpen(false)}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="p-4">
          {modalError && (
            <div className="mb-3 p-2.5 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-xs text-red-600">{modalError}</p>
            </div>
          )}
          {modalSuccess && (
            <div className="mb-3 p-2.5 bg-emerald-50 border border-emerald-100 rounded-lg">
              <p className="text-xs text-emerald-600">{modalSuccess}</p>
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); saveDept(); }} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Department Code
              </label>
              <input
                type="text"
                value={deptCode}
                onChange={(e) => setDeptCode(e.target.value)}
                placeholder="e.g., D001, HR, IT"
                disabled={!!editingDept}
                className={`w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition ${
                  editingDept ? 'bg-gray-50 text-gray-500' : ''
                }`}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Department Name
              </label>
              <input
                type="text"
                value={deptName}
                onChange={(e) => setDeptName(e.target.value)}
                placeholder="Enter department name"
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={deptStatus}
                onChange={(e) => setDeptStatus(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
              >
                <option value="A">Active</option>
                <option value="I">Inactive</option>
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={modalLoading}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {modalLoading ? (
                  <>
                    <RefreshCw size={12} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Stats Card Component
function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", text: "text-blue-600" },
    green: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", text: "text-emerald-600" },
    gray: { bg: "bg-gray-50", iconBg: "bg-gray-100", text: "text-gray-600" },
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`${colors.bg} border border-gray-100 rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className={`${colors.iconBg} p-1.5 rounded-lg`}>
          <Icon size={14} className={colors.text} />
        </div>
      </div>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600 mt-0.5">{label}</p>
    </div>
  );
}

// Department Card Component (Mobile View)
function DepartmentCard({ department, onEdit, index }) {
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3 hover:shadow-sm transition">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex px-1.5 py-0.5 text-xs font-mono font-medium bg-gray-100 text-gray-700 rounded">
              {department.code}
            </span>
            {department.status === "A" ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs">
                <CheckCircle size={8} />
                Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                <XCircle size={8} />
                Inactive
              </span>
            )}
          </div>
          <h3 className="text-sm font-medium text-gray-900">{department.name}</h3>
        </div>
        <button
          onClick={() => onEdit(department)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition"
        >
          <Pencil size={14} />
        </button>
      </div>
    </div>
  );
}

export default function AdminDepartment() {
  const dispatch = useDispatch();
  const { items: deptItems, loading, error } = useSelector(
    (state) => state.department || { items: [], loading: false, error: null }
  );

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptCode, setDeptCode] = useState("");
  const [deptName, setDeptName] = useState("");
  const [deptStatus, setDeptStatus] = useState("A");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  useEffect(() => {
    setDepartments(deptItems || []);
  }, [deptItems]);

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('card');
      } else {
        setViewMode('table');
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stats = useMemo(() => {
    const total = departments.length;
    const active = departments.filter(d => d.status === "A").length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [departments]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return departments.filter((d) => 
      d.name?.toLowerCase().includes(s) || 
      d.code?.toLowerCase().includes(s)
    );
  }, [departments, search]);

  const openAdd = () => {
    setEditingDept(null);
    setDeptCode("");
    setDeptName("");
    setDeptStatus("A");
    setModalError("");
    setModalSuccess("");
    setIsModalOpen(true);
  };

  const openEdit = (dept) => {
    setEditingDept(dept);
    setDeptCode(dept.code || "");
    setDeptName(dept.name);
    setDeptStatus(dept.status || "A");
    setModalError("");
    setModalSuccess("");
    setIsModalOpen(true);
  };

  const existingCodes = useMemo(() => {
    return departments.map((d) => (d.code || "").toLowerCase());
  }, [departments]);

  const saveDept = async () => {
    setModalError("");
    if (!deptCode.trim() || !deptName.trim()) {
      setModalError("Code and name are required.");
      return;
    }

    if (!editingDept && existingCodes.includes(deptCode.toLowerCase())) {
      setModalError("Department code already exists.");
      return;
    }

    setModalLoading(true);
    try {
      const payload = {
        code: deptCode,
        name: deptName,
        status: deptStatus,
      };
      const result = await postDepartment(payload);

      if (result?.statusCode === 200) {
        setModalSuccess("Saved successfully.");
        await dispatch(fetchDepartments());
        setTimeout(() => {
          setIsModalOpen(false);
          setModalSuccess("");
        }, 1200);
      } else {
        setModalError("Failed to save department.");
      }
    } catch (err) {
      console.error(err);
      setModalError(err?.response?.data?.message || err.message || "Save error");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Departments
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage your organization departments
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition shadow-sm"
          >
            <Plus size={14} />
            Add Department
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard 
          label="Total" 
          value={stats.total} 
          icon={Building2} 
          color="blue"
        />
        <StatCard 
          label="Active" 
          value={stats.active} 
          icon={CheckCircle} 
          color="green"
        />
        <StatCard 
          label="Inactive" 
          value={stats.inactive} 
          icon={XCircle} 
          color="gray"
        />
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search departments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
          />
        </div>
        
        {/* View Toggle - Only visible on desktop */}
        <div className="hidden sm:flex gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'table' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={12} className="inline mr-1" />
            Table
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'card' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid size={12} className="inline mr-1" />
            Cards
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={16} className="text-gray-400 animate-spin" />
            <span className="ml-2 text-xs text-gray-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 text-red-600 text-xs p-2 rounded-lg inline-block">
              {error}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center gap-1.5">
              <Building2 size={24} className="text-gray-300" />
              <p className="text-xs text-gray-500">No departments found</p>
              <button
                onClick={openAdd}
                className="mt-1 text-xs text-gray-900 hover:text-gray-700 underline"
              >
                Create your first department
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Table View - Desktop */}
            <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department Name
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((dept, idx) => (
                    <tr key={dept.id} className="hover:bg-gray-50/50 transition">
                      <td className="py-2.5 px-3 text-xs text-gray-500">
                        {idx + 1}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="inline-flex px-1.5 py-0.5 text-xs font-mono font-medium bg-gray-100 text-gray-700 rounded">
                          {dept.code}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="text-sm font-medium text-gray-900">
                          {dept.name}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        {dept.status === "A" ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs">
                            <CheckCircle size={10} />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                            <XCircle size={10} />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => openEdit(dept)}
                          className="inline-flex items-center gap-1 px-1.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition"
                        >
                          <Pencil size={12} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card View - Mobile */}
            <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-3 space-y-2`}>
              {filtered.map((dept, idx) => (
                <DepartmentCard 
                  key={dept.id} 
                  department={dept} 
                  onEdit={openEdit}
                  index={idx}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <DepartmentModal
          editingDept={editingDept}
          deptCode={deptCode}
          setDeptCode={setDeptCode}
          deptName={deptName}
          setDeptName={setDeptName}
          deptStatus={deptStatus}
          setDeptStatus={setDeptStatus}
          modalLoading={modalLoading}
          modalError={modalError}
          modalSuccess={modalSuccess}
          saveDept={saveDept}
          setIsModalOpen={setIsModalOpen}
        />
      )}
    </AdminLayout>
  );
}